/**
 * Widget Service: Handles HTTP communications with the MLC Ticketing Spoke REST API (bt_api)
 * and gathers browser diagnostic telemetry.
 */

export interface BrowserMetadata {
  url: string;
  pageTitle: string;
  userAgent: string;
  browser: string;
  os: string;
  screenResolution: string;
  viewportSize: string;
  timestamp: string;
}

export interface CreateIssuePayload {
  appId: string;
  appName?: string;
  title: string;
  category: "bug" | "feature_request" | "improvement";
  priority: "low" | "medium" | "high" | "critical";
  description: string;
  expectedBehavior?: string | null;
  url?: string;
  attachmentPaths?: string[];
  reporterEmail?: string;
  reporterName?: string;
  reporterUid?: string;
}

export interface CreateIssueResponse {
  status: string;
  issueId: string;
  issue: {
    id: string;
    title: string;
    status: string;
    priority: string;
    category: string;
    appId: string;
    appName: string;
    createdAt: string;
  };
}

export interface UserIssueSummary {
  id: string;
  title: string;
  status: string;
  priority: string;
  category: string;
  appId: string;
  appName: string;
  createdAt: string;
  updatedAt: string;
  description?: string;
  attachmentPaths?: string[];
  lastCommentAuthorEmail?: string;
  lastCommentAuthorRole?: string;
  lastUpdatedByEmail?: string;
  lastUpdatedByRole?: string;
}

export const DEFAULT_API_BASE_URL = "https://bt-api-qbuhtyitjq-uc.a.run.app";

/**
 * Extracts human-readable browser and operating system details from the user agent.
 */
export function detectBrowserAndOs(userAgent: string): {
  browser: string;
  os: string;
} {
  let browser = "Unknown Browser";
  let os = "Unknown OS";

  if (!userAgent) {
    return { browser, os };
  }

  // OS Detection
  if (/Windows/i.test(userAgent)) {
    os = "Windows";
  } else if (/Macintosh|Mac OS X/i.test(userAgent)) {
    os = "macOS";
  } else if (/Linux/i.test(userAgent)) {
    os = "Linux";
  } else if (/Android/i.test(userAgent)) {
    os = "Android";
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    os = "iOS";
  }

  // Browser Detection
  if (/Edg\//i.test(userAgent)) {
    browser = "Microsoft Edge";
  } else if (/Chrome\//i.test(userAgent) && !/Edg\//i.test(userAgent)) {
    browser = "Google Chrome";
  } else if (/Safari\//i.test(userAgent) && !/Chrome\//i.test(userAgent)) {
    browser = "Apple Safari";
  } else if (/Firefox\//i.test(userAgent)) {
    browser = "Mozilla Firefox";
  } else if (/MSIE|Trident/i.test(userAgent)) {
    browser = "Internet Explorer";
  }

  return { browser, os };
}

/**
 * Collects client-side environment telemetry silently.
 */
export function collectBrowserMetadata(): BrowserMetadata {
  const isBrowser = typeof window !== "undefined";
  const userAgent = isBrowser
    ? window.navigator.userAgent
    : "Node.js Environment";
  const { browser, os } = detectBrowserAndOs(userAgent);

  return {
    url: isBrowser ? window.location.href : "https://localhost",
    pageTitle: isBrowser ? document.title : "Spoke Client",
    userAgent,
    browser,
    os,
    screenResolution:
      isBrowser && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : "Unknown",
    viewportSize: isBrowser
      ? `${window.innerWidth}x${window.innerHeight}`
      : "Unknown",
    timestamp: new Date().toISOString(),
  };
}

/**
 * Submits a new bug or feedback issue to bt_api.
 */
export async function submitIssueViaApi(
  payload: CreateIssuePayload,
  apiBaseUrl: string = DEFAULT_API_BASE_URL,
  authToken?: string | null,
): Promise<CreateIssueResponse> {
  const endpoint = `${apiBaseUrl.replace(/\/+$/, "")}/api/v1/issues`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  if (payload.reporterEmail) {
    headers["X-Caller-Email"] = payload.reporterEmail;
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let errorMessage = `API error (${response.status})`;
    try {
      const errorJson = (await response.json()) as { error?: string };
      if (errorJson.error) {
        errorMessage = errorJson.error;
      }
    } catch {
      // Fallback to status text
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const rawData = (await response.json()) as {
    success?: boolean;
    status?: string;
    issueId?: string;
    issue?: {
      id?: string;
      title?: string;
      status?: string;
      priority?: string;
      category?: string;
      appId?: string;
      appName?: string;
      createdAt?: string;
    };
  };

  const resolvedId = rawData.issueId || rawData.issue?.id || "MLC-LOGGED";

  return {
    status: rawData.status || (rawData.success ? "success" : "created"),
    issueId: resolvedId,
    issue: {
      id: resolvedId,
      title: rawData.issue?.title || payload.title,
      status: rawData.issue?.status || "new",
      priority: rawData.issue?.priority || payload.priority,
      category: rawData.issue?.category || payload.category,
      appId: rawData.issue?.appId || payload.appId,
      appName: rawData.issue?.appName || payload.appName || payload.appId,
      createdAt: rawData.issue?.createdAt || new Date().toISOString(),
    },
  };
}

/**
 * Fetches recent issues submitted by a given user email for the current app.
 */
export async function fetchUserIssuesViaApi(
  email: string,
  appId?: string,
  apiBaseUrl: string = DEFAULT_API_BASE_URL,
  authToken?: string | null,
): Promise<UserIssueSummary[]> {
  if (!email) {
    return [];
  }

  const queryParams = new URLSearchParams({
    email: email.trim().toLowerCase(),
  });
  const endpoint = `${apiBaseUrl.replace(/\/+$/, "")}/api/v1/issues/user?${queryParams.toString()}`;

  const headers: Record<string, string> = {};
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  const response = await fetch(endpoint, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    let errorMessage = `Failed to fetch user issues (${response.status})`;
    try {
      const errorJson = (await response.json()) as { error?: string };
      if (errorJson.error) {
        errorMessage = errorJson.error;
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as { issues?: UserIssueSummary[] };
  const allIssues = Array.isArray(data.issues) ? data.issues : [];

  if (appId) {
    return allIssues.filter((i) => i.appId === appId);
  }

  return allIssues;
}

export interface IssueCommentSummary {
  id: string;
  issueId: string;
  authorName: string;
  authorEmail: string;
  authorRole: string;
  content: string;
  createdAt: string;
}

export interface AddIssueCommentPayload {
  authorEmail: string;
  authorName?: string;
  content: string;
  attachmentPaths?: string[];
}

export interface AddIssueCommentResponse {
  success: boolean;
  comment: IssueCommentSummary;
}

/**
 * Fetches discussion comments for a specific issue from bt_api.
 */
export async function fetchIssueCommentsViaApi(
  issueId: string,
  apiBaseUrl: string = DEFAULT_API_BASE_URL,
  authToken?: string | null,
): Promise<IssueCommentSummary[]> {
  if (!issueId) {
    return [];
  }

  const endpoint = `${apiBaseUrl.replace(/\/+$/, "")}/api/v1/issues/${encodeURIComponent(issueId)}/comments`;
  const headers: Record<string, string> = {};
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  const response = await fetch(endpoint, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    let errorMessage = `Failed to fetch comments (${response.status})`;
    try {
      const errorJson = (await response.json()) as { error?: string };
      if (errorJson.error) {
        errorMessage = errorJson.error;
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as { comments?: IssueCommentSummary[] };
  return Array.isArray(data.comments) ? data.comments : [];
}

/**
 * Adds a new comment or reply to an issue via bt_api.
 */
export async function addIssueCommentViaApi(
  issueId: string,
  payload: AddIssueCommentPayload,
  apiBaseUrl: string = DEFAULT_API_BASE_URL,
  authToken?: string | null,
): Promise<AddIssueCommentResponse> {
  const endpoint = `${apiBaseUrl.replace(/\/+$/, "")}/api/v1/issues/${encodeURIComponent(issueId)}/comments`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }
  if (payload.authorEmail) {
    headers["X-Caller-Email"] = payload.authorEmail;
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let errorMessage = `Failed to send comment (${response.status})`;
    try {
      const errorJson = (await response.json()) as { error?: string };
      if (errorJson.error) {
        errorMessage = errorJson.error;
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as AddIssueCommentResponse;
}
